/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

import { AviPermissionResource, IReport } from 'generated-types';
import { Component, Type } from '@angular/core';
import { IHttpResponse, IPromise } from 'angular';
import { Item, ObjectTypeItem } from 'ajs/modules/data-model/factories';
import { Report } from 'object-types';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';
import { withFullModalMixin } from 'ajs/js/utilities/mixins';

// We are getting report_uuid as response from save api.
interface IReportExtended extends IReport {
    report_uuid: string;
}

interface IIReportData {
    config: IReportExtended;
}

export enum ReportTypes {
    applicationSecurity = 'application_security',
    wafEvaluation = 'waf_pov',
}

export enum ReportDuration {
    reportPeriodLast7Days = 'REPORT_PERIOD_LAST_7_DAYS',
}

export type TReportItem = typeof ReportItem;

/**
 * @description Report Item
 * @author Deepak Chaudhary, Shahab Hashmi
 */
export class ReportItem extends withFullModalMixin(ObjectTypeItem) {
    public getConfig: () => IReport;
    public data: IIReportData;

    constructor(args = {}) {
        const extendedArgs = {
            objectName: 'report',
            objectType: Report,
            windowElement: 'lazy-load',
            permissionName: AviPermissionResource.PERMISSION_REPORT,
            ...args,
        };

        super(extendedArgs);
    }

    /** @override */
    protected saveRequest(): IPromise<any> {
        this.busy = true;
        this.errors = null;

        const headers: ng.IHttpRequestConfigHeaders = {};
        const url = 'api/report';

        return this.request<IReport>('post', url, super.dataToSave(), headers)
            .then((value: IHttpResponse<IReport>) => {
                this.busy = false;

                return value;
            })
            .catch(({ data }) => {
                this.busy = false;
                this.errors = data.status;
            });
    }

    /**
     * @override
     */
    // eslint-disable-next-line no-underscore-dangle
    protected getIdFromData_(data: IIReportData): string {
        return data?.config?.uuid ?? data?.config?.report_uuid ?? '';
    }

    /**
     * Import lazy-loaded modal component.
     */
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const { ReportModal } = await import(
            /* webpackChunkName: "report-modal" */
            'ng/lazy-loaded-components/modals/report-modal/report-modal.component'
        );

        return ReportModal as Type<Component>;
    }

    /**
     * Return the Report's filename.
     */
    public getFileName(): string {
        const fileUrl = this.data?.config?.filename?.split('/') ?? [];

        return fileUrl[fileUrl.length - 1] ?? '-';
    }

    /**
     * Return the Report's Reason.
     */
    public getReason(): string {
        const state = this.data?.config.state as Item;

        if (!state) {
            return '-';
        }

        return state.config?.reason;
    }
}
